import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { FaArrowLeft } from "react-icons/fa";
import { Button } from "./ui/Button";
import { useUser } from "./UserContext";
import { useFinderApi } from "./useFinderApi";
import { Toaster ,toast } from "react-hot-toast";

 const eventLabels = {
    reservation_status_placed: "Client successfully checked in to a reserved space",
    reservation_canceled_by_shelter: "Reservation canceled by shelter",
  };

export default function UserSettings() {
  const [editingPhone, setEditingPhone] = useState(false);
  const { user, setUser, loading } = useUser();
  const { updateUserDetails, updateNotificationPreferences, getNotificationEnums } = useFinderApi();
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifError, setNotifError] = useState("");
  const [initialPhone, setInitialPhone] = useState("");
  const [notificationEnums, setNotificationEnums] = useState({ event_types: [], channel_types: [] });
  const [initialPreferences, setInitialPreferences] = useState({});
  const [workingPrefs, setWorkingPrefs] = useState({});
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (user) {
      setPhone(user.phone || "");
      setInitialPhone(user.phone || "");
      const deepClone = (obj) => JSON.parse(JSON.stringify(obj));
      const prefs = user.preferences ? deepClone(user.preferences) : {};
      setInitialPreferences(prefs);
      setWorkingPrefs(prefs);
    }
  }, [user]);

  useEffect(() => {
    getNotificationEnums()
      .then((data) => {
        setNotificationEnums({
          event_types: data.notification_event_types || [],
          channel_types: data.notification_channel_types || [],
        });
      })
      .catch(() => {
        setNotificationEnums({ event_types: [], channel_types: [] });
      });
  }, [getNotificationEnums]);

  const handleSaveAll = async () => {
    setNotifError("");
    setPhoneError(false);
    setSaving(true);
    try {
      const updatedPrefs = await updateNotificationPreferences(
        user.id,
        workingPrefs
      );
      let updatedUser = { ...user, preferences: updatedPrefs.preferences };
      if (phone !== initialPhone) {
        if (!/^\+?[0-9]{10,15}$/.test(phone)) {
          setPhoneError(true);
          setSaving(false);
          return;
        }
        await updateUserDetails({ ...user, phone });
        updatedUser.phone = phone;
        setInitialPhone(phone);
      }
      setUser(updatedUser);
      setInitialPreferences(updatedPrefs.preferences);
      setWorkingPrefs(updatedPrefs.preferences);
      toast.success("Your notification preferences have been updated", { position: "top-right" });
    } catch (err) {
      setNotifError("Failed to update notification preferences.");
      setPhoneError(true);
    }
    setSaving(false);
  };

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-500">Loading user data...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-500">User data not available.</div>
      </div>
    );
  }

  const handleSavePhone = async () => {
    setPhoneError(false);
    if (!/^\(\d{3}\)\s?\d{3}-\d{4}$/.test(phone)) {
      setPhoneError(true);
      return;
    }
    try {
      // await validatePhoneWithTwilio(phone);
      await updateUserDetails({ ...user, phone });
      setUser({ ...user, phone });
      setInitialPhone(phone);
      setEditingPhone(false);
    } catch (err) {
      setPhoneError(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f8fa] pb-10">
      <header className="sticky top-0 z-30 bg-slate-100 shadow-md border-b border-gray-200 w-full">
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ minHeight: "56px" }}
        >
          <img
            src="/logo-shelterready.png"
            alt="Shelter Ready Logo"
            className="h-9 w-auto"
          />
          <div
            className="flex items-center gap-3 sm:gap-4 text-sm text-sr-blue relative"
            ref={menuRef}
          >
            <div
              className="w-9 h-9 rounded-full bg-slate-300 flex items-center justify-center text-white text-lg font-bold cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Open menu"
            >
              {user?.name && typeof user.name === "string"
                ? user.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                : "U"}
            </div>
            {menuOpen && (
              <div className="absolute right-0 top-10 bg-white border rounded shadow-lg z-10 min-w-[120px]">
                <button
                  onClick={() => {
                    navigate("/settings");
                    setMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 border-b"
                >
                  My Settings
                </button>
                <button
                  onClick={() =>
                    logout({
                      logoutParams: {
                        returnTo: `${window.location.origin}/login`,
                      },
                    })
                  }
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <Toaster
        toastOptions={{
          duration: 7000,
          style: {
            width: "500px",
            background: "#caced2",
            color: "#324158",
            fontWeight: "bold",
          },
        }}
      />

      <main className="max-w-3xl mx-auto pt-6 sm:pt-8 px-2 sm:px-0">
        <div className="mb-3">
          <button
            onClick={() => {
              setPhone(initialPhone);
              setPhoneError(false);
              setEditingPhone(false);
              setNotifError("");
              setSaving(false);
              setWorkingPrefs(initialPreferences);
              navigate("/needs");
            }}
            className="flex items-center border border-blue-500 text-blue-500 hover:border-gray-500 hover:text-gray-600  bg-white font-bold rounded-lg mb-4 px-3 py-2 transition"
          >
            <FaArrowLeft className="mr-2" /> Back to Need List
          </button>
        </div>
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-1">My Settings</h1>
          <p className="text-gray-500 text-sm">Manage your account settings and preferences</p>
        </div>

        {/* Personal Information Card */}
        <section className="bg-white rounded-xl shadow-lg border border-gray-200 mb-8">
          <div className="px-3 sm:px-8 pt-5 sm:pt-6 pb-2">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-0">Account Information</h2>
            <p className="text-gray-500 text-xs sm:text-sm mb-2">Your account details and identification information</p>
            <hr className="border-t border-gray-200 mb-4" />
            <div className="divide-y divide-gray-100">
              <div className="flex flex-col sm:flex-row items-start sm:items-center py-3 sm:py-4">
                <span className="w-full sm:w-48 text-gray-700 text-base font-normal mb-1 sm:mb-0">Full name</span>
                <span className="text-gray-900 text-base font-normal">{user?.name}</span>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center py-3 sm:py-4">
                <span className="w-full sm:w-48 text-gray-700 text-base font-normal mb-1 sm:mb-0">Email address</span>
                <span className="text-gray-900 text-base font-normal">{user?.email}</span>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center py-3 sm:py-4">
                <span className="w-full sm:w-48 text-gray-700 text-base font-normal mb-1 sm:mb-0">Organization</span>
                <span className="text-gray-900 text-base font-normal">{user?.organization_name}</span>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center py-3 sm:py-4">
                <span className="w-full sm:w-48 text-gray-700 text-base font-normal mb-1 sm:mb-0">
                  Phone number
                </span>

                <div className="flex flex-1 flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {editingPhone ? (
                    <>
                      <div className="flex flex-row items-start gap-2">
                        <div className="flex flex-col w-40 sm:w-48">
                          <input
                            type="tel"
                            inputMode="tel"
                            className={`p-1 border rounded flex-shrink-0 w-full focus:ring-2 focus:ring-sr-blue outline-none text-sm
                              ${phoneError ? 'border-red-500' : 'border-slate-400'}`}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            maxLength={16}
                            minLength={11}
                            required
                            disabled={saving}
                          />
                          {phoneError && (
                            <span className="mt-1 text-red-500 text-xs break-words">
                              Please enter it in standard format such as (123) 456-7890.
                            </span>
                          )}
                        </div>
                        <div className="flex flex-row gap-2 items-center">
                          <button
                            className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 border border-gray-300"
                            onClick={handleSavePhone}
                            type="button"
                            disabled={saving}
                          >
                            Save
                          </button>
                          <button
                            className="px-3 py-1 text-sm bg-white text-gray-500 rounded border border-gray-300 hover:bg-gray-100"
                            onClick={() => {
                              setEditingPhone(false);
                              setPhone(initialPhone);
                              setPhoneError(false);
                            }}
                            type="button"
                            disabled={saving}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex w-full items-center gap-2">
                      <span className="text-gray-900 text-base font-medium">{phone}</span>
                      <button
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded border border-gray-300 hover:bg-gray-200"
                        onClick={() => setEditingPhone(true)}
                        type="button"
                        disabled={saving}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Notification Preferences Card */}
        <section className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="px-3 sm:px-8 pt-5 sm:pt-6 pb-2">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-0">Notification preferences</h2>
            <p className="text-gray-500 text-xs sm:text-sm mb-2">Manage how and when you receive updates</p>
            <hr className="border-t border-gray-200 mb-4" />
            <div className="divide-y divide-gray-100">
              {notificationEnums.event_types.length > 0 && notificationEnums.channel_types.length > 0 &&
                notificationEnums.event_types.map((event) => {
                  const label = eventLabels[event] || event.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                  return (
                    <div key={event} className="flex flex-col sm:flex-row items-start sm:items-center py-3 sm:py-4">
                      <span className="flex-1 text-gray-700 text-base font-normal mb-2 sm:mb-0">{label}</span>
                      <div className="flex flex-wrap gap-2 sm:gap-4 w-full sm:w-auto">
                        {notificationEnums.channel_types.map((channel) => {
                          const isChecked =
                            workingPrefs &&
                            workingPrefs[event] &&
                            workingPrefs[event].includes(channel);
                          return (
                            <label key={channel} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  let newPrefs = { ...workingPrefs };
                                  let prev = Array.isArray(newPrefs[event]) ? newPrefs[event] : [];
                                  if (isChecked) {
                                    newPrefs[event] = prev.filter((c) => c !== channel);
                                  } else {
                                    newPrefs[event] = [...prev, channel];
                                  }
                                  setWorkingPrefs(newPrefs);
                                }}
                                className="w-4 h-4 accent-blue-600 mr-2 font-medium"
                              />
                            <span className="text-gray-700 text-base mr-3">
                              {channel === "sms"
                                ? "Text"
                                : channel.charAt(0).toUpperCase() + channel.slice(1)}
                            </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              }
            </div>
            <hr className="border-t border-gray-200 mb-4" />
            <p className="text-gray-500 text-sm">Selecting any text notification option means you agree to receive automated text messages from Shelter Ready at the phone number provided. Message and data rates may apply.</p>
            <div className="flex flex-col sm:flex-row justify-end pt-6 gap-2 sm:gap-0">
              <Button
                onClick={handleSaveAll}
                disabled={saving || !user}
                className="text-base bg-blue-500 hover:bg-blue-600 rounded-lg shadow text-white font-medium px-4 py-2"
              >
                {saving ? "Saving..." : "Save Preferences"}
              </Button>
              {notifError && (
                <div className="text-red-500 text-sm mt-2">{notifError}</div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
